import { Suspense, useContext, useMemo } from 'react';
import { SkeletonMainLayout } from 'src/modules/common/skeletons/SkeletonMainLayout/SkeletonMainLayout.tsx';
import { Query } from 'src/modules/common/components/Query/Query.tsx';
import { skipToken } from '@apollo/client';
import {
    CheckoutPageData,
    CheckoutPageVariables,
    getCheckoutPageConfig,
} from 'src/modules/checkout/CheckoutPageMapping.ts';
import { useNavigate, useParams } from 'react-router-dom';
import { ConfigContext } from 'src/configContext.ts';
import { useFilterLogic } from 'src/modules/common/hooks/useFilterLogic.ts';
import { useCheckMainFilter } from 'src/modules/common/hooks/useCheckMainFilter.ts';
import { CheckoutPage } from 'src/modules/checkout/CheckoutPage.tsx';
import { isOCCOSelected } from 'src/modules/common/isOCCOSelected.ts';
import { useUserInputState } from '../user-input/useUserInputState';
import { convert } from '../user-input/convert';
import { getUnitByName } from '../user-input/getUnitByName';
import { targetUnits } from '../user-input/targetUnits';

export const PrefetchValvesCheckout = () => {
    const {
        valveLabel,
        actuatorLabel,
    } = useParams();

    if (!valveLabel) {
        throw new Error('No Valve selected');
    }
    if (!actuatorLabel) {
        throw new Error('No Actuator selected');
    }

    const { userSettings } = useContext(ConfigContext);
    const pageConfig = useMemo(() => getCheckoutPageConfig(userSettings.currentRegion), [userSettings]);
    if (pageConfig === null) {
        throw new Error('No Page Config!');
    }

    const navigate = useNavigate();

    const [userInput] = useUserInputState([]);

    const [
        searchParams,
        filter,
    ] = useFilterLogic([
        ...pageConfig.mainFilterConfig,
        ...pageConfig.additionalFilterConfig,
        ...pageConfig.actuatorsFilterConfig,
    ]);

    useCheckMainFilter({
        filter,
        searchParams,
        navigate,
        filterConfig: [],
    });

    const variables = useMemo((): { variables: CheckoutPageVariables } | typeof skipToken => {
        if (filter === null) {
            return skipToken;
        }
        return {
            variables: {
                actuatorLabel,
                valveLabel,
                locale: userSettings.currentLocale,
                isOCCO: isOCCOSelected(filter),
                kvs: userInput.kvs.length > 0 ? convert(
                    userInput.kvs,
                    getUnitByName(userInput.kvs_u, targetUnits.volumetricFlow),
                    targetUnits.volumetricFlow,
                ) : '',
                volumetricFlow: userInput.v100.length > 0 ? convert(
                    userInput.v100,
                    getUnitByName(userInput.v100_u, targetUnits.volumetricFlow),
                    targetUnits.volumetricFlow,
                ) : '',
                dpmv: userInput.dpmv.length > 0 ? convert(
                    userInput.dpmv,
                    getUnitByName(userInput.dpmv_u, targetUnits.dpmv),
                    targetUnits.dpmv,
                ) : '',
            },
        };
    }, [
        filter,
        actuatorLabel,
        valveLabel,
        userSettings,
        userInput.kvs,
        userInput.kvs_u,
        userInput.v100,
        userInput.v100_u,
        userInput.dpmv,
        userInput.dpmv_u,
    ]);

    return (
        <Suspense fallback={<SkeletonMainLayout breadcrumbs={false}/>}>
            <Query<CheckoutPageData, CheckoutPageVariables>
                query={pageConfig.query}
                variables={variables}
            >
                {({ data }) => {
                    if (data === undefined) {
                        return (<SkeletonMainLayout/>);
                    }
                    return (
                        <CheckoutPage
                            navigate={navigate}
                            searchParams={searchParams}
                            data={data}
                        />
                    );
                }}
            </Query>
        </Suspense>
    );
};
